"use client";

import { useState } from "react";
import { CaptureField } from "./CaptureField";

interface FieldConfig {
  fieldKey: string;
  label: string;
  fieldType: string;
  options: unknown | null;
  placeholder: string | null;
  mandatory: boolean;
}

interface ScriptSectionProps {
  title: string;
  scriptText: string | null;
  sectionType: string;
  linkedFields: FieldConfig[];
  sectionNotes: string;
  onNotesChange: (notes: string) => void;
  qualificationData: Record<string, unknown>;
}

export function ScriptSection({ title, scriptText, sectionType, linkedFields, sectionNotes, onNotesChange, qualificationData }: ScriptSectionProps) {
  const [showNotes, setShowNotes] = useState(false);

  // Section type badge colors
  const typeColors: Record<string, string> = {
    introduction: "bg-blue-100 text-blue-700",
    consent: "bg-purple-100 text-purple-700",
    pre_vet: "bg-green-100 text-green-700",
    closing: "bg-gray-100 text-gray-700",
    custom: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-xs font-semibold text-gray-900">{title}</h3>
        <span className={`rounded-full px-2 py-0.5 text-[9px] font-medium ${typeColors[sectionType] ?? typeColors.custom}`}>
          {sectionType}
        </span>
      </div>

      {scriptText && (
        <p className="mb-3 text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">{scriptText}</p>
      )}

      {linkedFields.length > 0 && (
        <div className="space-y-2 mb-2">
          {linkedFields.map((field) => (
            <CaptureField
              key={field.fieldKey}
              fieldKey={field.fieldKey}
              label={field.label}
              fieldType={field.fieldType}
              options={field.options}
              placeholder={field.placeholder}
              mandatory={field.mandatory}
              initialValue={qualificationData[field.fieldKey]}
            />
          ))}
        </div>
      )}

      <button
        onClick={() => setShowNotes(!showNotes)}
        className="text-[10px] text-gray-400 hover:text-gray-600"
      >
        {showNotes ? "Hide notes" : "+ Add section notes"}
      </button>
      {showNotes && (
        <textarea
          value={sectionNotes}
          onChange={(e) => onNotesChange(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-xs resize-none"
          placeholder="Notes for this section..."
        />
      )}
    </div>
  );
}
